import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import { TErrorDetails } from "components/ProjectNonSpecificComponents/Elements/UploadButton/Interfaces"
import UploadButton from "components/ProjectNonSpecificComponents/Elements/UploadButton/UploadButton"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import { ACCEPTED_ATTACHMENT_EXT_AND_MIME_TYPES } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import mapValuesOrKeysFromObject from "components/ProjectSpecificComponents/Functions/mapValuesOrKeysFromObject"
import { observer } from "mobx-react"
import React, { forwardRef, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import IconButton from "./Buttons/IconButton"

interface Props {
	customPersonalizationDisabled?: boolean
	files?: File[]
	onChangeFiles?: (files: File[]) => void
	disabled?: boolean
}

const UploadAttachmentsInput = observer(
	forwardRef<HTMLDivElement, Props>(
		(
			{
				customPersonalizationDisabled = false,
				files,
				onChangeFiles = () => {},
				disabled,
				...props
			},
			ref
		) => {
			const { t } = useTranslation()
			const store = useStore()
			const [addedFiles, setAddedFiles] = useState<File[]>([])
			const [error, setError] = useState<TErrorDetails>()
			const mimeExt: string[] = mapValuesOrKeysFromObject(ACCEPTED_ATTACHMENT_EXT_AND_MIME_TYPES, false)
			
			useEffect(() => {
				if (files == null) return
				setAddedFiles(files)
			}, [files])
			
			useEffect(() => {
				error && store.setInfoModalConfig({
					type: "warning",
					titleKey: `${t("file.fail.reject")}`,
					descriptionKey: `${t("file.fail.reject-ext")} ${error.acceptedFileExts}`,
				})
			}, [error])

			const removeAddedFile = (index: number) => {
				setAddedFiles((addedFiles) => {
					let newAddedFiles = [...addedFiles.slice(0, index), ...addedFiles.slice(index + 1)]
					onChangeFiles(newAddedFiles)
					return newAddedFiles
				})
			}

			return (
				<>
					{addedFiles.length != 0 && (
						<>
							<HyperDiv
								style={{
									border: "1px solid var(--gray-c2)",
									borderRadius: 5,
								}}
								centeredY
							>
								<HyperDiv
									direction="column"
									padding="5px"
									style={{
										overflow: "hidden",
									}}
									spaceBetweenElems=".625rem"
								>
									{addedFiles.map(({ name }, i) => {
										return (
											<HyperDiv
												width="fit-content"
												direction="row"
												padding="1px 8px"
												spaceBetweenElems="1rem"
												style={{
													overflow: "hidden",
													backgroundColor: "var(--gray-ec)",
													borderRadius: 5,
													maxWidth: "100%",
												}}
												key={i}
											>
												<HyperDiv hidesOverflownText className="font-size-16">
													{name}
												</HyperDiv>
												<IconButton
													iconWidth={10}
													iconHeight={10}
													iconKey="cross"
													iconColor="var(--muddy-puddle-gray)"
													iconColorOnHover="var(--gray)"
													onClick={() => removeAddedFile(i)}
												/>
											</HyperDiv>
										)
									})}
								</HyperDiv>
							</HyperDiv>
							<Whitespace x=".625" />
						</>
					)}
					<HyperDiv
						style={{
							height: 38,
							border: "1px solid var(--gray-c2)",
							borderRadius: 5,
						}}
						centered
					>
						<UploadButton
							{...props}
							containerStyle={{ width: "100%", height: "100%" }}
							files={addedFiles}
							disabled={disabled}
							onUpdateFiles={(files) => {
								setAddedFiles(files)
								onChangeFiles(files)
							}}
							accept={mimeExt}
							onError={(error) => setError(error)}
						>
							<HyperDiv height="100%" centered direction="row" spaceBetweenElems=".75rem" >
							     {t("rawText.upload")}
								<IconButton iconWidth={15} iconHeight={15} iconKey="upload" />
							</HyperDiv>
						</UploadButton>
					</HyperDiv>
				</>
			)
		}
	)
)

export default UploadAttachmentsInput
